import type { Bonus, PromotionBonusOffersPending } from '@promotion/types'
import { clearNuxtData, useApi, useFormat, useMutation, useNuxtData, useQuery, useRewards, useUserInfo, toValue, isRefOrGetter } from '#imports'
import type { UseMutationMutateOptions, UseQueryFunctionOptions } from '@core/types'

export type BonusId = string | number

export const useBonus = () => {
    const api = useApi({
        enableLegacySupport: true
    })

    const { isLoggedIn, status: userInfoStatus } = useUserInfo()

    const { asMoney, asPercent } = useFormat()
    const { handlePromotionCookie } = useRewards()

    const isPercentage = (bonus: Bonus) => Number(bonus.percentage) > 0,
        isAmount = (bonus: Bonus) => Number(bonus.amount) > 0,
        isPending = (bonus: Bonus) => bonus.statusId === 1, // PENDING_TERMS_NOT_ACCEPTED
        isActive = (bonus: Bonus) => bonus.statusId === 2, // PENDING_TERMS_ACCEPTED
        minPaymentAmount = (bonus: Bonus) => asMoney(Number(bonus.min_payment_amount || 0), { decimals: 0 })

    function resetPendingBonus() {
        const { data: pendingBonus } = useNuxtData('promotions:bonus:pending')

        if (pendingBonus.value?.id) {
            handlePromotionCookie(pendingBonus.value.id.toString())
        }

        clearNuxtData('promotions:bonus:pending')
    }

    function amount(bonus: Bonus) {
        if (isPercentage(bonus)) {
            return Number(bonus.percentage)
        }

        if (isAmount(bonus)) {
            return Number(bonus.amount)
        }

        return 0
    }

    function formattedAmount(bonus: Bonus) {
        if (isPercentage(bonus)) {
            return asPercent(amount(bonus) / 100)
        }

        if (isAmount(bonus)) {
            return asMoney(amount(bonus), { decimals: 0 })
        }

        return 0
    }

    function bonusPlusMinPayment(bonus: Bonus) {
        if (isAmount(bonus)) {
            return asMoney(Number(bonus.min_payment_amount) + Number(bonus.amount), { decimals: 0 })
        }

        return 0
    }

    function minTurnoverAmount(bonus: Bonus) {
        if (!bonus) {
            return
        }

        return (Number(bonus.min_payment_amount) + Number(bonus.amount)) * Number(bonus.turnover_multiplier)
    }

    function winCap(bonus: Bonus) {
        if (!bonus) {
            return
        }

        return Number(bonus.win_cap)
    }

    function useGetPendingBonus(options: Pick<UseQueryFunctionOptions<Bonus>, 'enabled'> = {}) {
        return useQuery(async () => {
            const response = await api<PromotionBonusOffersPending>('/bonus_offers/pending')

            return response.bonus
        }, {
            key: 'promotions:bonus:pending',
            server: false,
            lazy: true,
            enabled: () => {
                if (!isLoggedIn.value) return false
                if (userInfoStatus.value === 'pending') return false
                if (options.enabled === false) return false

                if (isRefOrGetter(options.enabled)) {
                    return toValue(options.enabled)
                }

                return options.enabled ?? true
            }
        })
    }

    function useRejectPendingBonus(options: UseMutationMutateOptions = {}) {
        return useMutation((body: { bonusId: BonusId }) => {
            return api('/bonus_offers/reject_bonus', {
                method: 'post',
                body
            })
        }, options)
    }

    function useAcceptPendingBonus(options: UseMutationMutateOptions = {}) {
        return useMutation((body: { bonusId: BonusId }) => {
            return api('/bonus_offers/accept_bonus_terms', {
                method: 'post',
                body
            })
        }, options)
    }

    return {
        isPercentage,
        isAmount,
        isPending,
        isActive,
        amount,
        minPaymentAmount,
        formattedAmount,
        bonusPlusMinPayment,
        minTurnoverAmount,
        winCap,
        useGetPendingBonus,
        useRejectPendingBonus,
        useAcceptPendingBonus,
        resetPendingBonus,
    }
}
